/**
 * Client wave clock.
 *
 * Advances locally every frame (so wave animation is smooth at render rate) and
 * gently rate-corrects toward the authoritative `server_time` without ever
 * jumping. All clients share the same wave table, so matching time means
 * matching wave shape.
 *
 * Pure (no DOM), so `tests/unit.spec.ts` exercises it directly.
 */
export class WaveClock {
  time = 0;
  private started = false;

  reset(): void {
    this.time = 0;
    this.started = false;
  }

  /**
   * @param delta       Seconds since the previous frame.
   * @param serverTime  Latest authoritative time, or null before the first patch.
   * @param frozen      When true (reduced motion / paused) the clock does not advance.
   */
  update(delta: number, serverTime: number | null, frozen = false): void {
    if (serverTime !== null && !this.started) {
      this.time = serverTime;
      this.started = true;
      return;
    }
    if (frozen) return;

    if (serverTime === null) {
      this.time += delta;
      return;
    }

    // Rate-correct: play slightly fast when behind, slow when ahead. Clamped so
    // the clock always advances forwards and never snaps.
    const error = serverTime - this.time;
    const rate = Math.min(1.5, Math.max(0.5, 1 + error * 0.5));
    this.time += delta * rate;
  }
}
